// src/features/project/ItemDetailRail.tsx
// Right-rail detail for a single ProjectItem. Opening an item must not throw away
// the board/backlog, so this is a rail, not a page. Refreshes the item via GetItem
// on open and wires the item-level commands: UpdateItem, MoveItem, RemoveItem, and
// (un)assign to sprint / epic / component.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { projectApi } from '@core/api';
import { useI18n, useProjectItem, useProjectSprint, useProjectEpic, useProjectComponent } from '@react/hooks';
import { Link } from 'react-router-dom';
import { TaskSplitDetail } from '@/components/TaskSplitDetail';
import { SegmentedControl } from '@/components/form/SegmentedControl';
import { TabIdProvider, useTabId } from '@/components/tabs/TabContext';
import { Button, Input, Badge, ErrorAlert } from '@react/components';
import { Select } from '@/components/Select';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Field } from './ProjectModal';
import { entityRef, idOf, listParams } from './meta';
import { TaskReference } from '@/features/references/TaskReference';
import { ActorReference } from '@/features/references/ActorReference';
import { taskStatusMap } from '@/features/task/logic';
import type { Entity } from '@core/types/entity';
import type { ProjectItem, BoardColumn, Sprint, Epic, ProjectComponent } from '@core/types/project';

interface ItemDetailRailProps {
  projectEntity: Entity;
  item: ProjectItem;
  columns: BoardColumn[];
  onClose: () => void;
  onChanged: () => void;
  embedded?: boolean;
}

const ProjectItemPlacement = observer(
  ({ projectEntity, item, columns, onClose, onChanged, embedded = false }: ItemDetailRailProps) => {
    const { t } = useI18n();
    const itemStore = useProjectItem();
    const sprintStore = useProjectSprint();
    const epicStore = useProjectEpic();
    const componentStore = useProjectComponent();

    const projectId = projectEntity.id;
    const itemId = idOf(item);

    const [live, setLive] = useState<ProjectItem>(item);
    const [sprints, setSprints] = useState<Sprint[]>([]);
    const [epics, setEpics] = useState<Epic[]>([]);
    const [components, setComponents] = useState<ProjectComponent[]>([]);
    const [points, setPoints] = useState<string>(item.story_points != null ? String(item.story_points) : '');
    const [size, setSize] = useState<string>(item.t_shirt_size ?? '');
    const [busy, setBusy] = useState(false);
    const [actionError, setActionError] = useState<unknown>(null);
    const [confirmRemove, setConfirmRemove] = useState(false);

    // Refresh the item on open — the list row may be stale.
    useEffect(() => {
      let alive = true;
      setLive(item);
      setPoints(item.story_points != null ? String(item.story_points) : '');
      setSize(item.t_shirt_size ?? '');
      projectApi
        .GetItem(projectId, itemId)
        .then((response) => {
          const full = response.item;
          if (alive && full && full.entity) {
            setLive(full);
            setPoints(full.story_points != null ? String(full.story_points) : '');
            setSize(full.t_shirt_size ?? '');
          }
        })
        .catch(() => {});
      return () => {
        alive = false;
      };
    }, [item, itemId, projectId]);

    useEffect(() => {
      let alive = true;
      sprintStore.ListSprints(projectId, listParams()).then(() => alive && setSprints([...sprintStore.items]));
      epicStore.ListEpics(projectId, listParams()).then(() => alive && setEpics([...epicStore.items]));
      componentStore
        .ListComponents(projectId, listParams())
        .then(() => alive && setComponents([...componentStore.items]));
      return () => {
        alive = false;
      };
    }, [componentStore, epicStore, projectId, sprintStore]);

    const run = async (fn: () => Promise<unknown>) => {
      setBusy(true);
      setActionError(null);
      try {
        const result = await fn();
        if (result && typeof result === 'object' && 'item' in result) {
          const updated = (result as { item?: ProjectItem }).item;
          if (updated?.entity) setLive(updated);
        }
        onChanged();
      } catch (error) {
        setActionError(error);
      } finally {
        setBusy(false);
      }
    };

    const saveMetadata = () =>
      run(() =>
        itemStore.UpdateItem(projectId, itemId, {
          item: {
            ...live,
            entity: live.entity,
            project: projectEntity,
            story_points: points === '' ? undefined : Number(points),
            t_shirt_size: size,
          },
          field_mask: { paths: ['story_points', 't_shirt_size'] },
        } as never),
      );

    const moveColumn = (columnId: string) =>
      run(() =>
        itemStore.MoveItem(projectId, itemId, {
          project: projectEntity,
          item: live.entity!,
          to_column_id: columnId,
          position: live.position ?? 0,
        } as never),
      );

    const assignSprint = (sprintId: string) => {
      const currentId = live.sprint?.id;
      if (sprintId === (currentId ?? '')) return;
      run(async () => {
        if (!sprintId) {
          if (currentId) return sprintStore.RemoveItemFromSprint(projectId, currentId, itemId);
          return;
        }
        return sprintStore.AddItemToSprint(projectId, sprintId, {
            project: projectEntity,
            sprint: entityRef(sprintId, 'sprint', projectEntity),
            item: live.entity!,
        } as never);
      });
    };

    const assignEpic = (epicId: string) => {
      const currentId = live.epic?.id;
      if (epicId === (currentId ?? '')) return;
      run(async () => {
        if (!epicId) {
          if (currentId) return epicStore.RemoveItemFromEpic(projectId, currentId, itemId);
          return;
        }
        return epicStore.AddItemToEpic(projectId, epicId, {
            project: projectEntity,
            epic: entityRef(epicId, 'epic', projectEntity),
            item: live.entity!,
        } as never);
      });
    };

    const currentComponentIds = new Set((live.components ?? []).map((c) => c.id));
    const toggleComponent = (componentId: string) => {
      run(async () => {
        if (currentComponentIds.has(componentId)) {
          await componentStore.RemoveItemFromComponent(projectId, componentId, itemId);
        } else {
          await componentStore.AddItemToComponent(projectId, componentId, {
            project: projectEntity,
            component: entityRef(componentId, 'project_component', projectEntity),
            item: live.entity!,
          } as never);
        }
      });
    };

    const remove = () =>
      run(async () => {
        await itemStore.RemoveItem(projectId, itemId);
        setConfirmRemove(false);
        onClose();
      });

    return (
      <aside
        className="flex h-full w-full flex-col border-l border-rail-border bg-rail text-rail-foreground"
        aria-label={t('project.item.detail')}
      >
        {!embedded && <header className="flex items-start justify-between gap-2 border-b border-rail-border px-4 py-3">
          <div className="min-w-0">
            {live.item_key && (
              <span className="font-mono text-xs text-muted-foreground">{live.item_key}</span>
            )}
            <h2 className="truncate text-sm font-semibold text-foreground">
              {live.source_task?.id ? <TaskReference id={live.source_task.id} fallbackTitle={live.title} showKey={false} /> : live.title || t('common.untitled')}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </header>}

        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
          {actionError != null && <ErrorAlert error={actionError} dismissible onDismiss={() => setActionError(null)} />}
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            {live.status && <Badge variant="secondary" size="sm">{taskStatusMap[live.status] ? t(taskStatusMap[live.status].labelKey) : live.status}</Badge>}
            {live.assignee && <ActorReference actor={live.assignee} />}
          </div>

          {/* Move column */}
          {columns.length > 0 && (
            <Field label={t('project.item.column')} htmlFor="rail-column">
              <Select
                id="rail-column"
                value={live.column_id ?? ''}
                disabled={busy}
                onChange={(e) => moveColumn(e.target.value)}
                aria-label={t('project.item.column')}
              >
                <option value="">{t('project.item.noColumn')}</option>
                {columns.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}

          {/* Sprint */}
          <Field label={t('project.item.sprint')} htmlFor="rail-sprint">
            <Select
              id="rail-sprint"
              value={live.sprint?.id ?? ''}
              disabled={busy}
              onChange={(e) => assignSprint(e.target.value)}
              aria-label={t('project.item.sprint')}
            >
              <option value="">{t('project.item.backlogOption')}</option>
              {sprints.map((s) => (
                <option key={idOf(s)} value={idOf(s)}>
                  {s.name}
                </option>
              ))}
            </Select>
          </Field>

          {/* Epic */}
          <Field label={t('project.item.epic')} htmlFor="rail-epic">
            <Select
              id="rail-epic"
              value={live.epic?.id ?? ''}
              disabled={busy}
              onChange={(e) => assignEpic(e.target.value)}
              aria-label={t('project.item.epic')}
            >
              <option value="">{t('project.item.noEpic')}</option>
              {epics.map((ep) => (
                <option key={idOf(ep)} value={idOf(ep)}>
                  {ep.name}
                </option>
              ))}
            </Select>
          </Field>

          {/* Components */}
          {components.length > 0 && (
            <Field label={t('project.item.components')}>
              <div className="flex flex-wrap gap-1.5">
                {components.map((c) => {
                  const on = currentComponentIds.has(idOf(c));
                  return (
                    <button
                      key={idOf(c)}
                      type="button"
                      disabled={busy}
                      onClick={() => toggleComponent(idOf(c))}
                      aria-pressed={on}
                      className={`rounded-full border px-2.5 py-1 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                        on
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border text-muted-foreground hover:bg-muted'
                      }`}
                    >
                      {c.name}
                    </button>
                  );
                })}
              </div>
            </Field>
          )}

          {/* Estimation */}
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('project.item.storyPoints')} htmlFor="rail-points">
              <Input
                id="rail-points"
                type="number"
                inputMode="numeric"
                value={points}
                onChange={(e) => setPoints(e.target.value)}
                placeholder="—"
              />
            </Field>
            <Field label={t('project.item.size')} htmlFor="rail-size">
              <Input id="rail-size" value={size} onChange={(e) => setSize(e.target.value)} placeholder="—" />
            </Field>
          </div>
          <Button size="sm" variant="secondary" onClick={saveMetadata} disabled={busy}>
            {t('project.item.saveMetadata')}
          </Button>

          {/* Labels (read-only display) */}
          {(live.labels?.length ?? 0) > 0 && (
            <Field label={t('project.item.labels')}>
              <div className="flex flex-wrap gap-1.5">
                {live.labels.map((l) => (
                  <span
                    key={idOf(l) || l.name}
                    className="rounded-full px-2 py-0.5 text-xs text-white"
                    style={{ backgroundColor: l.color || undefined }}
                  >
                    {l.name}
                  </span>
                ))}
              </div>
            </Field>
          )}
        </div>

        <footer className="border-t border-rail-border px-4 py-3">
          <Button size="sm" variant="danger" onClick={() => setConfirmRemove(true)} disabled={busy}>
            {t('project.item.remove')}
          </Button>
        </footer>

        <ConfirmDialog
          open={confirmRemove}
          title={t('project.item.removeConfirmTitle')}
          description={t('project.item.removeConfirmDesc')}
          destructive
          isConfirming={busy}
          onConfirm={remove}
          onCancel={() => setConfirmRemove(false)}
        />
      </aside>
    );
  },
);

ProjectItemPlacement.displayName = 'ProjectItemPlacement';

// A ProjectItem is a placement/projection; TaskService owns the work itself.
// Keep its entity/resource context separate from the project pane, including
// attachments, so opening a preview cannot replace the board's right-rail data.
export const ItemDetailRail = observer(function ItemDetailRail(props: ItemDetailRailProps) {
  const { t } = useI18n();
  const tabId = useTabId();
  const [view, setView] = useState('task');
  const taskId = props.item.source_task?.id;
  const itemId = idOf(props.item);
  useEffect(() => { setView('task'); }, [itemId]);
  if (!taskId) return <ProjectItemPlacement {...props} />;
  const contextId = `${tabId}:project-item:${itemId}`;
  return (
    <section className="flex h-full min-h-0 flex-col bg-background text-foreground" aria-label={t('task.splitDetail')}>
      <header className="flex shrink-0 items-center gap-2 border-b border-border px-3 py-2">
        <SegmentedControl size="sm" value={view} onChange={setView} aria-label={t('project.item.previewView')} options={[
          { value: 'task', label: t('project.item.taskPreview') },
          { value: 'placement', label: t('project.item.placement') },
        ]} />
        {props.item.item_key && <span className="min-w-0 flex-1 truncate text-xs tabular-nums text-muted-foreground">{props.item.item_key}</span>}
        <Link to={`/tasks/${taskId}`} aria-label={t('task.openFull')} title={t('task.openFull')} className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14 21 3" /></svg>
        </Link>
        <Button size="sm" variant="ghost" onClick={props.onClose} aria-label={t('common.close')}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
        </Button>
      </header>
      <div className="min-h-0 flex-1">
        {view === 'task' ? (
          <TabIdProvider value={contextId}>
            <TaskSplitDetail key={taskId} taskId={taskId} onClose={props.onClose} onUpdated={props.onChanged} showHeader={false} keyboardShortcuts={false} />
          </TabIdProvider>
        ) : <ProjectItemPlacement key={itemId} {...props} embedded />}
      </div>
    </section>
  );
});
